/**
 * Utilities for converting between frequencies and musical notes
 */

import { NoteName } from "./types";

const A4_FREQUENCY = 440; // Standard tuning reference
const NOTE_NAMES = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];

/**
 * Convert frequency to MIDI note number
 * @param frequency Frequency in Hz
 * @returns MIDI note number (69 = A4)
 */
export function frequencyToMidi(frequency: number): number {
  return 69 + 12 * Math.log2(frequency / A4_FREQUENCY);
}

/**
 * Convert MIDI note number to frequency
 * @param midiNote MIDI note number
 * @returns Frequency in Hz
 */
export function midiToFrequency(midiNote: number): number {
  return A4_FREQUENCY * Math.pow(2, (midiNote - 69) / 12);
}

/**
 * Get the nearest MIDI note number for a frequency
 * @param frequency Frequency in Hz
 * @returns Nearest MIDI note number
 */
export function getNearestMidiNote(frequency: number): number {
  return Math.round(frequencyToMidi(frequency));
}

/**
 * Get note name from MIDI number
 * @param midiNote MIDI note number
 * @returns Note name with octave (e.g., "C4", "A#5")
 */
export function midiToNoteName(midiNote: number): string {
  const noteIndex = midiNote % 12;
  const octave = Math.floor(midiNote / 12) - 1;
  return `${NOTE_NAMES[noteIndex]}${octave}`;
}

/**
 * Convert frequency to note name
 * @param frequency Frequency in Hz
 * @returns Note name with octave
 */
export function frequencyToNoteName(frequency: number): string {
  const midiNote = getNearestMidiNote(frequency);
  return midiToNoteName(midiNote);
}

/**
 * Calculate cents deviation from the nearest note
 * Cents are 1/100th of a semitone
 * @param frequency Frequency in Hz
 * @returns Cents deviation (positive = sharp, negative = flat)
 */
export function getCentsOffPitch(frequency: number): number {
  const midiFloat = frequencyToMidi(frequency);
  const nearestMidi = Math.round(midiFloat);
  const centsOff = (midiFloat - nearestMidi) * 100;
  return centsOff;
}

/**
 * Get detailed note information from a frequency
 * @param frequency Frequency in Hz
 * @returns Object with note name, MIDI number, and cents deviation
 */
export function analyzeFrequency(frequency: number) {
  const midiFloat = frequencyToMidi(frequency);
  const nearestMidi = Math.round(midiFloat);
  const centsOff = (midiFloat - nearestMidi) * 100;
  const noteName = midiToNoteName(nearestMidi);
  const exactFrequency = midiToFrequency(nearestMidi);

  return {
    frequency,
    noteName,
    midiNote: nearestMidi,
    centsOff,
    nearestFrequency: exactFrequency,
  };
}

/**
 * Check if a frequency is within a certain range of a target note
 * @param frequency Frequency to check
 * @param targetNote Note name (e.g., "C4")
 * @param toleranceCents Tolerance in cents (default 50 = half semitone)
 * @returns True if within range
 */
export function isNearNote(
  frequency: number,
  targetNote: string,
  toleranceCents: number = 50
): boolean {
  const detectedNote = frequencyToNoteName(frequency);
  if (detectedNote !== targetNote) {
    return false;
  }
  const centsOff = Math.abs(getCentsOffPitch(frequency));
  return centsOff <= toleranceCents;
}

export function accidental(n: NoteName): 'sharp' | 'flat' | undefined {
  if (n.includes('#')) {
    return 'sharp';
  }
  if (n.includes('b')) {
    return 'flat';
  }
}
